'use strict'

const { relatedSpecFromKey } = require('./related-view-columns')

// Excel-style pivot. Rows and columns are group labels. Each value is an
// aggregation of the source records in that intersection. Totals are
// aggregated from those records, so an average total is not an average of
// averages.

const AGGREGATIONS = ['sum', 'count', 'average', 'min', 'max']

const AGGREGATION_LABELS = {
  sum: 'Sum',
  count: 'Count',
  average: 'Average',
  min: 'Min',
  max: 'Max'
}

const BLANK_LABEL = '(blank)'

const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/

const SYSTEM_PIVOT_FIELDS = new Set(['id', 'createdAt', 'updatedAt'])

const BLOCKED_PIVOT_TYPES = {
  password: 'a password column',
  image: 'an image column',
  file: 'a file column',
  relationship: 'a relationship column',
  richText: 'a rich text column',
  signature: 'a signature column'
}

const PATH_SEP = '\u0001'

function isEnabled (value, fallback) {

  if (value === undefined || value === null) return fallback

  return value === true || value === 'true'
}

function isStoredPivotField (name) {

  return FIELD_NAME.test(name || '') || !!relatedSpecFromKey(name)
}

function fieldList (values) {

  const names = []
  const seen = new Set()

  for (const value of values || []) {

    const name = typeof value === 'string' ? value : (value && value.field)

    if (!isStoredPivotField(name)) continue
    if (seen.has(name)) continue

    seen.add(name)
    names.push(name)
  }

  return names
}

function valueList (values) {

  const list = []
  const seen = new Set()

  for (const value of values || []) {

    if (!value || !isStoredPivotField(value.field)) continue

    const aggregation = AGGREGATIONS.includes(value.aggregation) ? value.aggregation : 'sum'
    const key = `${value.field}:${aggregation}`

    if (seen.has(key)) continue

    seen.add(key)

    const label = typeof value.label === 'string' ? value.label.trim().slice(0, 80) : ''

    list.push({ field: value.field, aggregation, label })
  }

  return list
}

function pivotFieldProblem (field, columns, relatedKeys) {

  const name = typeof field === 'string' ? field.trim() : ''

  if (!name) return 'Choose a column.'

  if (relatedSpecFromKey(name)) {

    if (!relatedKeys) return ''

    const allowed = relatedKeys instanceof Set ? relatedKeys : new Set(relatedKeys)

    if (allowed.has(name)) return ''

    return `"${name}" is not a field reached from this spreadsheet.`
  }

  if (!FIELD_NAME.test(name)) {

    return `"${name}" is not a column name this pivot can store.`
  }

  if (SYSTEM_PIVOT_FIELDS.has(name)) return ''

  const column = (columns || []).find(item => item && item.uniqueTitle === name)

  if (!column) return `"${name}" is not a column on this spreadsheet.`

  const blocked = BLOCKED_PIVOT_TYPES[column.type]

  if (!blocked) return ''

  return `${column.title || name} cannot be used in a pivot because it is ${blocked}.`
}

function pivotSelectionProblems (config, columns, relatedKeys) {

  if (!Array.isArray(columns)) {

    return [{
      area: 'Pivot',
      field: '',
      message: 'The spreadsheet could not be loaded, so these fields cannot be saved yet.'
    }]
  }

  const source = config && typeof config === 'object' ? config : {}
  const areas = [
    ['Rows', source.rowFields],
    ['Columns', source.columnFields],
    ['Values', (source.valueFields || []).map(value => value && value.field)]
  ]
  const problems = []

  for (const [area, fields] of areas) {

    const seen = new Set()

    for (const field of fields || []) {

      const name = typeof field === 'string' ? field.trim() : ''

      if (!name || seen.has(name)) continue

      seen.add(name)

      const message = pivotFieldProblem(name, columns, relatedKeys)

      if (message) problems.push({ area, field: name, message })
    }
  }

  return problems
}

function normalizePivotConfig (component) {

  const source = component && typeof component === 'object' ? component : {}
  const rowFields = fieldList(source.rowFields)
  const columnFields = fieldList(source.columnFields)
  const valueFields = valueList(source.valueFields)

  return {
    rowFields,
    columnFields,
    valueFields,
    showSubtotals: isEnabled(source.showSubtotals, true),
    showRowGrandTotal: isEnabled(source.showRowGrandTotal, true),
    showColumnGrandTotal: isEnabled(source.showColumnGrandTotal, true),
    configured: rowFields.length > 0 || columnFields.length > 0 || valueFields.length > 0
  }
}

function pivotFieldNames (component) {

  const config = normalizePivotConfig(component)

  return [...new Set([
    ...config.rowFields,
    ...config.columnFields,
    ...config.valueFields.map(value => value.field)
  ])]
}

function pivotLabel (value) {

  if (value === null || value === undefined) return BLANK_LABEL
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (value instanceof Date) return value.toISOString()

  if (typeof value === 'string') {

    const trimmed = value.trim()

    return trimmed ? trimmed : BLANK_LABEL
  }

  if (Array.isArray(value)) {

    const parts = value.map(pivotLabel).filter(label => label !== BLANK_LABEL)

    return parts.length ? parts.join(', ') : BLANK_LABEL
  }

  return String(value)
}

function numericValue (value) {

  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'boolean' || value instanceof Date) return null
  if (typeof value !== 'string') return null

  const trimmed = value.trim()

  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) return null

  const parsed = Number(trimmed)

  return Number.isFinite(parsed) ? parsed : null
}

function datePoint (value) {

  if (value instanceof Date && !isNaN(value.getTime())) {

    return { time: value.getTime(), label: value.toISOString() }
  }

  if (typeof value !== 'string') return null

  const trimmed = value.trim()

  if (!/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return null

  const time = Date.parse(trimmed)

  if (!Number.isFinite(time)) return null

  return { time, label: trimmed }
}

function cleanNumber (value) {

  if (value === null || !Number.isFinite(value)) return null

  return Math.round(value * 1e10) / 1e10
}

function emptyStats () {

  return { rows: 0, sum: 0, numericCount: 0, nonBlank: 0, min: null, max: null, dateMin: null, dateMax: null }
}

function addStat (stats, raw) {

  stats.rows += 1

  if (raw !== null && raw !== undefined && !(typeof raw === 'string' && raw.trim() === '')) {

    stats.nonBlank += 1
  }

  const numeric = numericValue(raw)

  if (numeric !== null) {

    stats.sum += numeric
    stats.numericCount += 1
    stats.min = stats.min === null ? numeric : Math.min(stats.min, numeric)
    stats.max = stats.max === null ? numeric : Math.max(stats.max, numeric)

    return
  }

  const date = datePoint(raw)

  if (!date) return

  if (!stats.dateMin || date.time < stats.dateMin.time) stats.dateMin = date
  if (!stats.dateMax || date.time > stats.dateMax.time) stats.dateMax = date
}

function finishStat (stats, aggregation) {

  if (!stats || stats.rows === 0) return null

  if (aggregation === 'count') return stats.nonBlank
  if (aggregation === 'average') return stats.numericCount ? cleanNumber(stats.sum / stats.numericCount) : null
  if (aggregation === 'min') {

    if (stats.min !== null) return cleanNumber(stats.min)

    return stats.dateMin ? stats.dateMin.label : null
  }

  if (aggregation === 'max') {

    if (stats.max !== null) return cleanNumber(stats.max)

    return stats.dateMax ? stats.dateMax.label : null
  }

  return stats.numericCount ? cleanNumber(stats.sum) : 0
}

function pathKey (path) {

  return path.join(PATH_SEP)
}

function compareLabels (left, right) {

  if (left === right) return 0
  if (left === BLANK_LABEL) return 1
  if (right === BLANK_LABEL) return -1

  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

function comparePaths (left, right) {

  const length = Math.max(left.length, right.length)

  for (let index = 0; index < length; index += 1) {

    const compared = compareLabels(left[index], right[index])

    if (compared) return compared
  }

  return 0
}

function valueHeading (value, titles) {

  if (value.label) return value.label

  const name = (titles && titles[value.field]) || value.field

  return `${AGGREGATION_LABELS[value.aggregation] || 'Sum'} of ${name}`
}

function fieldHeading (field, titles) {

  return (titles && titles[field]) || field
}

function emptyPivot (config) {

  return {
    configured: !!(config && config.configured),
    empty: true,
    valueCaption: '',
    rowHeaders: [],
    headerRows: [],
    columns: [],
    values: [],
    body: []
  }
}

function buildPivot (records, component, titles) {

  const config = normalizePivotConfig(component)

  if (!config.configured) return emptyPivot(config)

  const rows = Array.isArray(records) ? records : []
  const values = config.valueFields.length
    ? config.valueFields
    : [{ field: null, aggregation: 'count', label: 'Count' }]
  const resolvedValues = values.map(value => ({
    field: value.field,
    aggregation: value.aggregation,
    label: value.field ? valueHeading(value, titles) : (value.label || 'Count')
  }))

  if (!rows.length) {

    const result = emptyPivot(config)

    result.values = resolvedValues

    return result
  }

  const buckets = new Map()
  const rowPathSet = new Map()
  const columnPathSet = new Map()

  function bucketFor (rowKey, columnKey) {

    const key = `${rowKey}\u0002${columnKey}`

    if (!buckets.has(key)) buckets.set(key, resolvedValues.map(() => emptyStats()))

    return buckets.get(key)
  }

  for (const record of rows) {

    const rowPath = config.rowFields.map(field => pivotLabel(record && record[field]))
    const columnPath = config.columnFields.map(field => pivotLabel(record && record[field]))

    rowPathSet.set(pathKey(rowPath), rowPath)
    columnPathSet.set(pathKey(columnPath), columnPath)

    const rowKeys = []
    const columnKeys = []

    for (let index = 0; index <= rowPath.length; index += 1) rowKeys.push(pathKey(rowPath.slice(0, index)))
    for (let index = 0; index <= columnPath.length; index += 1) columnKeys.push(pathKey(columnPath.slice(0, index)))

    for (const rowKey of rowKeys) {

      for (const columnKey of columnKeys) {

        const stats = bucketFor(rowKey, columnKey)

        resolvedValues.forEach((value, index) => {

          const raw = value.field ? record && record[value.field] : 1

          addStat(stats[index], value.field ? raw : 1)
        })
      }
    }
  }

  const columnPaths = config.columnFields.length
    ? [...columnPathSet.values()].sort(comparePaths)
    : [[]]
  const rowPaths = config.rowFields.length
    ? [...rowPathSet.values()].sort(comparePaths)
    : [[]]

  const showColumnGrandTotal = config.showColumnGrandTotal && config.columnFields.length > 0
  const columns = []

  for (const path of columnPaths) {

    resolvedValues.forEach((value, valueIndex) => {

      columns.push({ path, valueIndex, grand: false })
    })
  }

  if (showColumnGrandTotal) {

    resolvedValues.forEach((value, valueIndex) => {

      columns.push({ path: [], valueIndex, grand: true })
    })
  }

  function cellsFor (rowKey) {

    return columns.map(column => {

      const columnKey = column.grand ? '' : pathKey(column.path)
      const stats = buckets.get(`${rowKey}\u0002${columnKey}`)

      return finishStat(stats && stats[column.valueIndex], resolvedValues[column.valueIndex].aggregation)
    })
  }

  const body = []

  function pushRow (kind, labels, rowKey) {

    body.push({ kind, labels, cells: cellsFor(rowKey) })
  }

  if (!config.rowFields.length) {

    pushRow('detail', ['Total'], '')
  } else {

    function walk (depth, prefix) {

      const groups = new Map()

      for (const path of rowPaths) {

        if (prefix.some((part, index) => path[index] !== part)) continue

        const key = path[depth]

        if (!groups.has(key)) groups.set(key, key)
      }

      for (const key of groups.keys()) {

        const next = prefix.concat(key)

        if (depth === config.rowFields.length - 1) {

          pushRow('detail', next, pathKey(next))
          continue
        }

        walk(depth + 1, next)

        if (config.showSubtotals) {

          const labels = next.slice(0, -1).concat(`${key} Total`)

          while (labels.length < config.rowFields.length) labels.push('')

          pushRow('subtotal', labels, pathKey(next))
        }
      }
    }

    walk(0, [])

    if (config.showRowGrandTotal) {

      const labels = ['Grand Total']

      while (labels.length < config.rowFields.length) labels.push('')

      pushRow('grand', labels, '')
    }
  }

  const valueSpan = resolvedValues.length
  const headerRows = []

  if (!config.columnFields.length) {

    headerRows.push(resolvedValues.map(value => ({ label: value.label, colspan: 1, rowspan: 1 })))
  } else {

    for (let depth = 0; depth < config.columnFields.length; depth += 1) {

      const cells = []
      let index = 0

      while (index < columnPaths.length) {

        let next = index + 1

        while (
          next < columnPaths.length &&
          columnPaths[next].slice(0, depth + 1).every((part, partIndex) => part === columnPaths[index][partIndex])
        ) {

          next += 1
        }

        cells.push({
          label: columnPaths[index][depth],
          colspan: (next - index) * valueSpan,
          rowspan: 1
        })
        index = next
      }

      if (showColumnGrandTotal && depth === 0) {

        cells.push({
          label: 'Grand Total',
          colspan: valueSpan,
          rowspan: config.columnFields.length
        })
      }

      headerRows.push(cells)
    }

    if (resolvedValues.length > 1) {

      const cells = []

      for (let index = 0; index < columnPaths.length; index += 1) {

        for (const value of resolvedValues) cells.push({ label: value.label, colspan: 1, rowspan: 1 })
      }

      if (showColumnGrandTotal) {

        for (const value of resolvedValues) cells.push({ label: value.label, colspan: 1, rowspan: 1 })
      }

      headerRows.push(cells)
    }
  }

  const rowHeaders = config.rowFields.length
    ? config.rowFields.map(field => fieldHeading(field, titles))
    : ['']

  const valueCaption = resolvedValues.length === 1 && config.columnFields.length
    ? resolvedValues[0].label
    : ''

  return {
    configured: true,
    empty: false,
    valueCaption,
    rowHeaders,
    headerRows,
    columns,
    values: resolvedValues,
    body
  }
}

module.exports = {
  AGGREGATIONS,
  AGGREGATION_LABELS,
  BLANK_LABEL,
  normalizePivotConfig,
  pivotFieldProblem,
  pivotSelectionProblems,
  pivotFieldNames,
  pivotLabel,
  buildPivot
}
